// ============= Imports =============
import { useEffect } from 'react';
import { useSignalRHub } from '@/hooks/useSignalRHub';
import {
  createUserRelayHubHandlers,
  UserRelayHubOptions,
} from '@/handlers/signalR/userRelayHub';
import { useToastSafe } from '@/hooks/signalR/useToastSafe';
import { useWorkspaceErrorHandler } from '@/hooks/useWorkspaceErrorHandler';

// ============= Hook =============

/**
 * React hook that wires User relay hub SignalR handlers into the current component.
 *
 * This hook listens for user/workspace membership events (user added, removed, promoted,
 * demoted, reactivated, workspace added/updated) and delegates handling to the
 * `createUserRelayHubHandlers` factory.
 *
 * @param options - Configuration for user relay hub handling (workspace filter, callbacks, logging, toasts).
 */

export const useUserRelayHubHandlers = (
  options: UserRelayHubOptions = {}
) => {
  const workspaceHub = useSignalRHub({ hubName: 'userRelayHub' });
  const { handleError } = useWorkspaceErrorHandler(options.workspaceId);
  const enableToasts = options.enableToasts !== false; // Default to true
  const toasts = useToastSafe(enableToasts);

  useEffect(() => {
    if (!workspaceHub.isConnected || !workspaceHub.on || !workspaceHub.off) {
      return;
    }

    const { subscribe, unsubscribe } = createUserRelayHubHandlers(
      {
        on: workspaceHub.on,
        off: workspaceHub.off,
      },
      {
        ...options,
        toasts,
      }
    );

    try {
      subscribe();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error('Failed to subscribe User relay hub handlers:', error);
      handleError(error, 'Failed to subscribe to user updates');
    }

    return () => {
      unsubscribe();
    };
  }, [workspaceHub.isConnected, workspaceHub.on, workspaceHub.off, handleError, options.workspaceId, options.onDataRefresh, options.onMessageReceived, options.onEventReceived, options.enableToasts, options.enableLogging, options.getDisplayNameForUser, options.invokeHub, options.currentUserId, options.currentWorkspaceId, options.onPresenceResponse, toasts, options]);
};

